import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Folder as FolderIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';
import { formatBytes } from '../utils/formatters';

const StorageSettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPaths, setCurrentPaths] = useState({
    os_images_path: '',
    disk_volumes_path: '',
    cloud_init_path: '',
    home_directories_path: ''
  });
  const [newPaths, setNewPaths] = useState({
    os_images_path: '',
    disk_volumes_path: '',
    cloud_init_path: '',
    home_directories_path: ''
  });
  const [validationResults, setValidationResults] = useState({});
  const [storageUsage, setStorageUsage] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [validating, setValidating] = useState({});

  const pathLabels = {
    os_images_path: 'OS 이미지 저장 경로',
    disk_volumes_path: '디스크 볼륨 저장 경로',
    cloud_init_path: 'Cloud-init 파일 저장 경로',
    home_directories_path: '홈 디렉토리 저장 경로'
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [pathsResponse, usageResponse] = await Promise.all([
        apiService.getStoragePaths(),
        apiService.getStorageUsage()
      ]);
      
      setCurrentPaths(pathsResponse.data);
      setNewPaths(pathsResponse.data);
      setStorageUsage(usageResponse.data.storage_usage || []);
    } catch (error) {
      
      enqueueSnackbar('스토리지 설정을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handlePathChange = (pathKey, value) => {
    setNewPaths(prev => ({
      ...prev,
      [pathKey]: value
    }));
    
    // 기존 유효성 검사 결과 제거
    setValidationResults(prev => {
      const updated = { ...prev };
      delete updated[pathKey];
      return updated;
    });
  };

  const validatePath = async (pathKey, path) => {
    if (!path.trim()) return;
    
    setValidating(prev => ({ ...prev, [pathKey]: true }));
    try {
      const response = await apiService.validateStoragePath(path);
      setValidationResults(prev => ({
        ...prev,
        [pathKey]: response.data
      }));
    } catch (error) {
      setValidationResults(prev => ({
        ...prev,
        [pathKey]: {
          path,
          valid: false,
          error: error.response?.data?.detail || error.message
        }
      }));
    } finally {
      setValidating(prev => ({ ...prev, [pathKey]: false }));
    }
  };

  const handleValidateAll = async () => {
    const validationPromises = Object.entries(newPaths).map(([key, value]) => 
      validatePath(key, value)
    );
    await Promise.all(validationPromises);
  };

  const handleSave = async () => {
    // 모든 경로 유효성 검사
    await handleValidateAll();
    
    // 유효성 검사 결과 확인
    const hasInvalidPaths = Object.values(validationResults).some(result => !result.valid);
    if (hasInvalidPaths) {
      enqueueSnackbar('유효하지 않은 경로가 있습니다. 경로를 확인해주세요.', { variant: 'error' });
      return;
    }
    
    setConfirmDialogOpen(true);
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      const response = await apiService.updateStoragePaths(newPaths);
      setCurrentPaths(newPaths);
      setConfirmDialogOpen(false);
      enqueueSnackbar('스토리지 경로가 성공적으로 업데이트되었습니다.', { variant: 'success' });
      
      // 사용량 정보 새로고침
      loadData();
    } catch (error) {
      
      enqueueSnackbar(`스토리지 경로 업데이트 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return Object.keys(newPaths).some(key => newPaths[key] !== currentPaths[key]);
  };

  const getValidationIcon = (pathKey) => {
    const result = validationResults[pathKey];
    if (validating[pathKey]) {
      return <CircularProgress size={20} />;
    }
    if (!result) return null;
    
    if (result.valid) {
      return <CheckCircleIcon color="success" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const getUsageColor = (usagePercent) => {
    if (usagePercent < 70) return 'success';
    if (usagePercent < 90) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">스토리지 설정</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 1 }}
          >
            새로고침
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges() || saving}
          >
            {saving ? '저장 중...' : '설정 저장'}
          </Button>
        </Box>
      </Box>

      {/* 현재 스토리지 사용량 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          스토리지 사용량
        </Typography>
        <Grid container spacing={3}>
          {storageUsage.map((usage) => (
            <Grid item xs={12} md={4} key={usage.path_name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {pathLabels[usage.path_name]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {usage.path}
                  </Typography>
                  
                  {usage.exists ? (
                    <>
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={usage.usage_percent}
                          color={getUsageColor(usage.usage_percent)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2">
                        {usage.used_gb}GB / {usage.total_gb}GB 사용 ({usage.usage_percent}%)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        여유 공간: {usage.free_gb}GB
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        파일 개수: {usage.file_count}개
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        디렉토리 크기: {usage.directory_size_gb}GB
                      </Typography>
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      경로가 존재하지 않습니다
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* 경로 설정 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          스토리지 경로 설정
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Docker 배포 환경 안내:</strong>
          </Typography>
          <Typography variant="body2">
            • 권장 경로: <code>/var/lib/libvirt/images</code> 하위 경로 사용
          </Typography>
          <Typography variant="body2">
            • 다른 경로를 사용하려면 배포 시 docker-compose.yml에 볼륨 마운트를 추가해야 합니다.
          </Typography>
          <Typography variant="body2">
            • gen_images_enhanced.sh 스크립트 실행 시 커스텀 경로를 설정할 수 있습니다.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {Object.entries(pathLabels).map(([pathKey, label]) => (
            <Grid item xs={12} key={pathKey}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <TextField
                  fullWidth
                  label={label}
                  value={newPaths[pathKey]}
                  onChange={(e) => handlePathChange(pathKey, e.target.value)}
                  onBlur={() => validatePath(pathKey, newPaths[pathKey])}
                  error={validationResults[pathKey] && !validationResults[pathKey].valid}
                  helperText={
                    validationResults[pathKey] && !validationResults[pathKey].valid
                      ? validationResults[pathKey].error
                      : `현재: ${currentPaths[pathKey]}`
                  }
                  InputProps={{
                    endAdornment: getValidationIcon(pathKey)
                  }}
                />
              </Box>
              
              {validationResults[pathKey] && validationResults[pathKey].valid && (
                <>
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      유효한 경로입니다. 여유 공간: {validationResults[pathKey].free_space_gb}GB
                      {validationResults[pathKey].is_mounted && (
                        <> • 컨테이너에 마운트됨 ✓</>
                      )}
                    </Typography>
                  </Alert>
                  {validationResults[pathKey].warning && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        {validationResults[pathKey].warning}
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleValidateAll}
            disabled={Object.keys(validating).some(key => validating[key])}
          >
            모든 경로 유효성 검사
          </Button>
        </Box>
      </Paper>

      {/* 확인 다이얼로그 */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>스토리지 경로 변경 확인</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            스토리지 경로를 변경하면 기존 파일들이 새 경로로 이동되지 않습니다. 
            수동으로 파일을 이동하거나 새 경로에 파일을 다시 업로드해야 합니다.
          </Alert>
          
          <Typography variant="h6" gutterBottom>변경 사항:</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>구분</TableCell>
                  <TableCell>기존 경로</TableCell>
                  <TableCell>새 경로</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(pathLabels).map(([pathKey, label]) => (
                  newPaths[pathKey] !== currentPaths[pathKey] && (
                    <TableRow key={pathKey}>
                      <TableCell>{label}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{currentPaths[pathKey]}</TableCell>
                      <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{newPaths[pathKey]}</TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>취소</Button>
          <Button 
            onClick={confirmSave} 
            variant="contained"
            disabled={saving}
          >
            {saving ? '저장 중...' : '확인'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StorageSettingsPage;